import classNames from 'classnames'
import { FC, useRef, useState } from 'react'
import useOutsideClick from 'src/hooks/useOutsideClick'
import {
  PromptListItem,
  PromptStatus,
} from 'src/services/aiListeningApi/brandProfile.types'
import styles from './promptList.module.scss'
import { ContextTags, SparkleIcon } from '../ContextTags'
import { Pill } from '../Pill'
import { formatDateTime } from '../utils'

interface MenuAction {
  label: string
  onSelect: () => void
}

interface PromptCardProps {
  onDelete: () => void
  onOpen: () => void
  onStatusChange: (status: PromptStatus) => void
  prompt: PromptListItem
}

export const PromptIcon: FC = () => (
  <svg fill="none" height="32" viewBox="0 0 32 32" width="32">
    <path
      d="M12 5l1.6 4.4L18 11l-4.4 1.6L12 17l-1.6-4.4L6 11l4.4-1.6L12 5z"
      fill="currentColor"
    />
    <path
      d="M24 4l.8 2.2L27 7l-2.2.8L24 10l-.8-2.2L21 7l2.2-.8L24 4zM7 20l.6 1.4L9 22l-1.4.6L7 24l-.6-1.4L5 22l1.4-.6L7 20z"
      fill="currentColor"
    />
    <rect
      height="12"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.8"
      width="11"
      x="15"
      y="15"
    />
    <path
      d="M18.5 19.5h4M18.5 22.5h2.5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.8"
    />
  </svg>
)

const STATUS_PILLS: Partial<Record<PromptStatus, string>> = {
  completed: 'Completed',
  draft: 'Draft',
}

// A prompt is rarely edited, but every AI check refreshes its results, so the
// newer of the two is when the card's data last changed.
const lastUpdated = (prompt: PromptListItem): string =>
  prompt.lastCheckedAt && prompt.lastCheckedAt > prompt.updatedAt
    ? prompt.lastCheckedAt
    : prompt.updatedAt

export const PromptCard: FC<PromptCardProps> = ({
  onDelete,
  onOpen,
  onStatusChange,
  prompt,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useOutsideClick({ onOutsideClick: () => setIsMenuOpen(false), ref: menuRef })

  const actions: MenuAction[] = [
    prompt.status !== 'active' && {
      label: 'Activate',
      onSelect: () => onStatusChange('active'),
    },
    prompt.status !== 'completed' && {
      label: 'Complete',
      onSelect: () => onStatusChange('completed'),
    },
    prompt.status !== 'draft' && {
      label: 'Move to draft',
      onSelect: () => onStatusChange('draft'),
    },
    { label: 'Delete', onSelect: onDelete },
  ].filter((action): action is MenuAction => !!action)

  const statusLabel = STATUS_PILLS[prompt.status]

  return (
    <article className={styles.card}>
      <div className={styles.cardTop}>
        <span className={styles.cardIcon}>
          <PromptIcon />
        </span>
        <div className={styles.cardBody}>
          <button className={styles.promptText} onClick={onOpen} type="button">
            „{prompt.prompt}„
          </button>
          {(statusLabel || prompt.checkState === 'pending') && (
            <span className={styles.cardPills}>
              {statusLabel && <Pill tone="grey">{statusLabel}</Pill>}
              {prompt.checkState === 'pending' && (
                <Pill dot tone="purple">
                  Collecting AI answers
                </Pill>
              )}
            </span>
          )}
        </div>
        <div className={styles.cardActions}>
          <button className={styles.viewButton} onClick={onOpen} type="button">
            View Prompt Details
          </button>
          <div className={styles.menuWrapper} ref={menuRef}>
            <button
              aria-expanded={isMenuOpen}
              aria-label="Prompt actions"
              className={styles.menuButton}
              onClick={() => setIsMenuOpen((open) => !open)}
              type="button"
            >
              <svg
                fill="currentColor"
                height="20"
                viewBox="0 0 20 20"
                width="20"
              >
                <circle cx="10" cy="4.5" r="1.6" />
                <circle cx="10" cy="10" r="1.6" />
                <circle cx="10" cy="15.5" r="1.6" />
              </svg>
            </button>
            {isMenuOpen && (
              <div className={styles.menu} role="menu">
                {actions.map((action) => (
                  <button
                    className={classNames(styles.menuItem, {
                      [styles.menuItemDanger]: action.label === 'Delete',
                    })}
                    key={action.label}
                    onClick={() => {
                      setIsMenuOpen(false)
                      action.onSelect()
                    }}
                    role="menuitem"
                    type="button"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.cardFooter}>
        <span className={styles.contextLabel}>
          <SparkleIcon />
          Detected Context
        </span>
        <span className={styles.footerTags}>
          {prompt.contextTags.length ? (
            <ContextTags tags={prompt.contextTags} />
          ) : (
            <span className={styles.noContext}>None detected</span>
          )}
        </span>
        <span className={styles.dates}>
          <span>
            Added <strong>{formatDateTime(prompt.createdAt)}</strong>
          </span>
          <span>
            Updated <strong>{formatDateTime(lastUpdated(prompt))}</strong>
          </span>
        </span>
      </div>
    </article>
  )
}
